<!--
  An invite link (/p/:token). The friend makes an account (a passkey) or signs in, and becomes
  that person in the group. After that they see their own items whenever they sign in.
-->
<script lang="ts">
  import * as H from "../settings/hints.ts";
  import FullName from "../components/FullName.svelte";
  import { api, ApiError, NetworkError } from "../lib/api.ts";
  import { colorOf } from "../lib/colors.ts";
  import { app } from "../lib/store.svelte.ts";
  import { member } from "../lib/member.svelte.ts";
  import { groupPath, router } from "../lib/router.svelte.ts";
  import { devSignIn, passkeyError, passkeysSupported, registerPasskey, signInWithPasskey } from "../lib/auth.ts";

  let { token }: { token: string } = $props();

  interface Info {
    group: { id: string; name: string };
    person: { name: string; color: string };
    status: "open" | "yours" | "used";
  }

  let info = $state<Info | null>(null);
  let error = $state("");
  let name = $state("");
  let busy = $state(false);
  let problem = $state("");
  const supported = passkeysSupported();
  const dev = import.meta.env.DEV;

  async function loadInfo() {
    try {
      info = await api<Info>(`/api/invite/${token}`);
      name ||= info.person.name;
    } catch (e) {
      error = e instanceof ApiError ? e.message : "You're offline. Open this link again when you're connected.";
    }
  }

  $effect(() => {
    void loadInfo();
  });

  async function finish(groupId: string) {
    await app.refreshAccount();
    await member.refreshGroups();
    router.go(groupPath(groupId));
  }

  async function accept() {
    const r = await api<{ groupId: string }>(`/api/invite/${token}/accept`, { body: {} });
    await finish(r.groupId);
  }

  async function run(fn: () => Promise<void>) {
    busy = true;
    problem = "";
    try {
      await fn();
    } catch (e) {
      problem = e instanceof NetworkError ? "You're offline. Try again when you're connected." : passkeyError(e);
    } finally {
      busy = false;
    }
  }

  const create = () =>
    run(async () => {
      await registerPasskey({ name: name.trim(), inviteToken: token });
      await finish(info!.group.id);
    });

  const signIn = () =>
    run(async () => {
      await signInWithPasskey();
      await app.refreshAccount();
      if (app.role === "member") await accept();
      else await loadInfo();
    });

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(location.href);
      app.toast("Copied the invite link");
    } catch {
      app.toast("Couldn't copy. Copy it from the address bar.", "error");
    }
  }
</script>

<div class="screen">
  <div class="card" style:--c={colorOf(info?.person.color)}>
    {#if error}
      <h1>This invite doesn't work</h1>
      <p class="muted">{error} Ask whoever sent it for a new one.</p>
    {:else if !info || !app.ready}
      <p class="muted">Loading…</p>
    {:else}
      <p class="eyebrow">{info.group.name}</p>
      <h1><span class="dot" aria-hidden="true"></span>{info.person.name}</h1>

      {#if info.status === "yours"}
        <p class="muted">You've already joined with this invite.</p>
        <button class="btn primary" onclick={() => router.go(groupPath(info!.group.id))}>Open {info.group.name}</button>
      {:else if info.status === "used"}
        <p class="muted">Someone already joined with this invite. If that was you, sign in.</p>
        {#if app.signedIn !== true}<button class="btn primary" disabled={busy || !supported} onclick={signIn}>Sign in with passkey</button>{/if}
      {:else if app.signedIn && app.role === "owner"}
        <p class="muted">This is {info.person.name}'s invite. You're the treasurer, so you already see everything. Send this link to them.</p>
        <button class="btn primary" onclick={copyLink}>Copy invite link</button>
      {:else if app.signedIn && app.role === "member"}
        <p class="muted">Join {info.group.name} as {info.person.name}? You'll see your items there alongside your other groups.</p>
        <button class="btn primary" disabled={busy} onclick={() => run(accept)}>Join {info.group.name}</button>
      {:else}
        <p class="muted">
          You've been added to {info.group.name}. Make an account to see your items, what you owe and your spending. It
          uses a passkey (Face ID, Touch ID or your phone), so there's no password.
        </p>
        <form onsubmit={(e) => { e.preventDefault(); void create(); }}>
          <FullName bind:value={name} label="Your name" firstPlaceholder={info.person.name.split(" ")[0]} lastPlaceholder="Last name" />
          <button class="btn primary" disabled={busy || !supported || !name.trim().includes(" ")}>Create account</button>
          {#if H.INVITE_NAME_NOTE.trim()}<p class="muted small">{H.INVITE_NAME_NOTE}</p>{/if}
        </form>
        <p class="alt">
          Already have an account from another group?
          <button class="link" disabled={busy || !supported} onclick={signIn}>Sign in</button>
        </p>
        {#if dev}<button class="btn quiet small" onclick={() => run(async () => { await devSignIn(name.trim() || "Friend"); await app.refreshAccount(); await accept(); })}>Developer join (localhost only)</button>{/if}
      {/if}
      {#if !supported}<p class="owes">This browser doesn't support passkeys. Try Safari, Chrome or Edge.</p>{/if}
      {#if problem}<p class="owes" role="alert">{problem}</p>{/if}
    {/if}
  </div>
</div>

<style>
  .screen {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 24px var(--gutter);
  }

  .card {
    width: min(440px, 100%);
    display: grid;
    gap: 14px;
    padding: 28px;
    border-radius: var(--r-lg);
    background: var(--surface);
    border: 1px solid var(--rule);
    border-top: 6px solid var(--c);
  }

  .eyebrow {
    margin: 0;
    color: var(--ink-2);
    font-weight: 600;
  }

  h1 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: var(--t-2xl);
  }

  .dot {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--c);
  }

  form {
    display: grid;
    gap: 12px;
  }

  .alt {
    margin: 0;
    font-size: var(--t-sm);
    color: var(--muted);
  }

  .link {
    padding: 0;
    border: 0;
    background: none;
    color: var(--accent, #3b7ddd);
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
</style>
